/**
 * MKS-345 — the instance-wide sales pipeline (`/api/lead-stages`).
 *
 * Deliberately the same shape as `services/api/projectStates.ts`, the board's
 * per-project column API, so `StateListEditor` / `AddStateColumn` drive both
 * surfaces through identical callbacks. The two differences are that a lead
 * stage is instance-wide (no project id in the path) and that it carries
 * meaning: a `kind` the backend's won/lost behaviour reads, and a win
 * `probability` the weighted column total multiplies by.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

export const LEAD_STAGE_KINDS = ['open', 'won', 'lost'] as const;
export type LeadStageKind = (typeof LEAD_STAGE_KINDS)[number];

export const LEAD_STAGE_KIND_LABELS: Record<LeadStageKind, string> = {
  open: 'Open',
  won: 'Won',
  lost: 'Lost',
};

export interface LeadStage {
  id: number;
  name: string;
  /** Slug mirror of `name` — the value the legacy `lead.stage` string holds. */
  slug: string;
  ordering: number;
  color: string; // '#RRGGBB'
  kind: LeadStageKind;
  /** Win probability in percent, 0-100. */
  probability: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateLeadStageInput {
  name: string;
  color: string;
  probability?: number;
}

export interface UpdateLeadStageInput {
  name?: string;
  color?: string;
  kind?: LeadStageKind;
  probability?: number;
}

export async function fetchLeadStages(): Promise<{ data: LeadStage[] }> {
  return apiGet('/lead-stages');
}

export async function createLeadStageApi(
  input: CreateLeadStageInput,
): Promise<{ data: LeadStage }> {
  return apiPost('/lead-stages', input);
}

export async function updateLeadStageApi(
  id: number,
  patch: UpdateLeadStageInput,
): Promise<{ data: LeadStage }> {
  return apiPatch(`/lead-stages/${id}`, patch);
}

export async function deleteLeadStageApi(id: number): Promise<void> {
  return apiDelete(`/lead-stages/${id}`);
}

/** Positional: the array IS the new order (index 0 becomes `ordering` 0). */
export async function reorderLeadStagesApi(
  stageIds: number[],
): Promise<{ data: LeadStage[] }> {
  return apiPatch('/lead-stages/reorder', { stageIds });
}

// ── Stage history ──────────────────────────────────────────────────────────

export interface LeadStageHistoryEntry {
  id: number;
  fromStage: { id: number; name: string } | null;
  toStage: { id: number; name: string } | null;
  changedBy: { id: number; firstName: string | null; lastName: string | null } | null;
  createdAt: string;
}

export async function fetchLeadStageHistory(
  leadId: number,
): Promise<{ data: LeadStageHistoryEntry[] }> {
  return apiGet(`/leads/${leadId}/stage-history`);
}

/**
 * The per-currency weighted value of a column: each raw total scaled by the
 * stage's win probability. The server returns this on `stageTotals`, but the
 * internal board recomputes its totals client-side after FX conversion (see
 * `pages/sales/LeadsPage.tsx`), so the arithmetic lives here too, in one place
 * rather than in each page.
 */
export function weightedValue(
  value: Record<string, number>,
  probability: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [currency, amount] of Object.entries(value)) {
    out[currency] = (amount * probability) / 100;
  }
  return out;
}
